import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { searchContent, topRatedMovies } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

const typeFilters = ["全部", "影视剧", "资讯"] as const;
const suggestions = ["悬疑", "反转", "白塔", "情感", "年代", "幕后"];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get("q") ?? "";
  const [query, setQuery] = useState(urlQuery);
  const [type, setType] = useState<(typeof typeFilters)[number]>("全部");
  const lastUrlQuery = useRef(urlQuery);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (urlQuery !== lastUrlQuery.current) {
      lastUrlQuery.current = urlQuery;
      setQuery(urlQuery);
    }
  }, [urlQuery]);

  const { movieResults, newsResults } = useMemo(() => searchContent(query), [query]);
  const total = type === "影视剧" ? movieResults.length : type === "资讯" ? newsResults.length : movieResults.length + newsResults.length;
  const showMovies = type !== "资讯";
  const showNews = type !== "影视剧";
  const hasQuery = query.trim().length > 0;

  usePageMeta({
    title: hasQuery
      ? `“${query.trim()}”的搜索结果 - “奸情”影视内容站`
      : "全站搜索 - “奸情”影视内容站",
    description: hasQuery
      ? `围绕“${query.trim()}”检索影视剧与资讯，共找到 ${movieResults.length + newsResults.length} 条相关内容，可按影视剧或资讯筛选。`
      : "输入关键词搜索影视剧名称、剧情介绍、演员或影视资讯，快速找到你想看的内容。",
    keywords: "影视搜索,剧集搜索,剧情解析搜索,影视资讯搜索",
  });

  const updateQuery = (value: string) => {
    setQuery(value);
    lastUrlQuery.current = value;
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set("q", value);
    } else {
      next.delete("q");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />
      <main className="w-full">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-8 pt-24 md:px-8 md:pt-32 lg:px-12">
          <div className="w-full">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />
            <h1 className="mt-5 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
              全站搜索
            </h1>
            <p className="mt-2 text-sm leading-7 text-foreground-600 md:text-[15px]">
              支持剧名、演员、类型、剧情关键词与资讯标题检索。
            </p>
          </div>
        </section>

        <div className="sticky top-16 z-30 w-full border-b border-background-200 bg-background-50/95 backdrop-blur-md md:top-20">
          <div className="w-full px-4 py-3 md:px-8 lg:px-12">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-lg" aria-hidden="true" />
                </span>
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(event) => updateQuery(event.target.value)}
                  placeholder="搜索影视剧、演员或资讯关键词"
                  aria-label="搜索关键词"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-11 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
                {hasQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      updateQuery("");
                      inputRef.current?.focus();
                    }}
                    className="absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-foreground-400 transition-colors hover:bg-background-200 hover:text-foreground-700"
                    aria-label="清空搜索关键词"
                  >
                    <i className="ri-close-line text-base" aria-hidden="true" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {typeFilters.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setType(item)}
                    className={`flex-1 rounded-full px-3 py-2 text-xs font-medium transition-colors whitespace-nowrap sm:flex-none md:text-sm ${
                      type === item ? "bg-primary-900 text-background-50" : "text-foreground-600 hover:text-primary-700"
                    }`}
                    aria-pressed={type === item}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <section className="w-full px-4 py-8 md:px-8 md:py-12 lg:px-12">
          {!hasQuery ? (
            <div className="flex flex-col gap-10">
              <div>
                <h2 className="font-heading text-base font-bold text-foreground-950">热门搜索</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {suggestions.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => updateQuery(item)}
                      className="rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-700 md:text-sm"
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-end justify-between">
                  <h2 className="font-heading text-base font-bold text-foreground-950">口碑推荐</h2>
                  <Link to="/category" className="text-xs text-primary-700 transition-colors hover:text-accent-600">
                    查看全部
                  </Link>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                  {topRatedMovies.map((movie, index) => (
                    <MovieCard key={movie.id} movie={movie} index={index} />
                  ))}
                </div>
              </div>
            </div>
          ) : total === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-2xl text-foreground-400">
                <i className="ri-search-eye-line" aria-hidden="true" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                没有找到与“{query.trim()}”相关的内容
              </h2>
              <p className="mt-2 max-w-md text-sm leading-6 text-foreground-500">
                试试更短的关键词，或者换成剧名、演员名。你也可以清空关键词，从下面这些口碑作品里重新开始。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <button
                  type="button"
                  onClick={() => updateQuery("")}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-900 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors whitespace-nowrap hover:bg-primary-800"
                >
                  <i className="ri-refresh-line text-base" aria-hidden="true" />
                  重新搜索
                </button>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors whitespace-nowrap hover:border-primary-300 hover:text-primary-700"
                >
                  浏览分类
                </Link>
              </div>

              <div className="mt-10 w-full text-left">
                <h3 className="font-heading text-sm font-bold text-foreground-950">也许你会喜欢</h3>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                  {topRatedMovies.map((movie, index) => (
                    <MovieCard key={movie.id} movie={movie} index={index} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-xs text-foreground-500 md:text-sm">
                关键词 <span className="font-heading font-bold text-primary-800">“{query.trim()}”</span> 共找到
                <span className="font-label mx-1 font-bold text-accent-700">{total}</span>条结果
                {type === "全部" && `（影视剧 ${movieResults.length} · 资讯 ${newsResults.length}）`}
              </p>

              {showMovies && movieResults.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-1 rounded-full bg-accent-500" />
                    <h2 className="font-heading text-base font-bold text-foreground-950">影视剧</h2>
                    <span className="font-label text-xs text-foreground-400">{movieResults.length}</span>
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
                    {movieResults.map((movie, index) => (
                      <MovieCard key={movie.id} movie={movie} index={index} />
                    ))}
                  </div>
                </div>
              )}

              {showNews && newsResults.length > 0 && (
                <div className="mt-10">
                  <div className="flex items-center gap-2">
                    <span className="h-4 w-1 rounded-full bg-accent-500" />
                    <h2 className="font-heading text-base font-bold text-foreground-950">资讯</h2>
                    <span className="font-label text-xs text-foreground-400">{newsResults.length}</span>
                  </div>
                  <div className="mt-4 flex flex-col gap-3">
                    {newsResults.map((article) => (
                      <Link
                        key={article.id}
                        to={`/news/${article.id}`}
                        className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:flex-row md:items-center md:gap-5"
                      >
                        <div className="h-[150px] w-full shrink-0 overflow-hidden rounded-md md:h-[92px] md:w-[150px]">
                          <img
                            src={article.cover}
                            alt={`${article.title}配图`}
                            title={`${article.title} - 搜索结果`}
                            className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-secondary-900">
                              {article.category}
                            </span>
                            <span className="font-label text-foreground-400">{article.date}</span>
                          </div>
                          <h3 className="mt-1.5 font-heading text-sm font-bold text-foreground-900 transition-colors group-hover:text-primary-700 md:text-base">
                            {article.title}
                          </h3>
                          <p className="mt-1 text-xs leading-6 text-foreground-500 line-clamp-2 md:text-sm">
                            {article.summary}
                          </p>
                        </div>
                        <i
                          className="ri-arrow-right-line hidden text-lg text-foreground-300 transition-colors group-hover:text-accent-500 md:block"
                          aria-hidden="true"
                        />
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {showMovies && movieResults.length === 0 && type === "影视剧" && (
                <p className="mt-8 rounded-lg border border-dashed border-background-300 px-5 py-10 text-center text-sm text-foreground-500">
                  影视剧分类下没有匹配结果，试试切换到「资讯」看看。
                </p>
              )}
              {showNews && newsResults.length === 0 && type === "资讯" && (
                <p className="mt-8 rounded-lg border border-dashed border-background-300 px-5 py-10 text-center text-sm text-foreground-500">
                  资讯分类下没有匹配结果，试试切换到「影视剧」看看。
                </p>
              )}
            </div>
          )}
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}